import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Sub-line under the title. Also used as the accessible description. */
  description?: string;
  children: ReactNode;
  /** Right-aligned footer actions. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Dark navy surface for call-related dialogs. */
  tone?: 'default' | 'popup';
  /** Hide the top-right close button (forced-choice dialogs). */
  hideClose?: boolean;
  className?: string;
}

const SIZE = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
} as const;

/**
 * Centered dialog. Use `Sheet`-style side panels only where the spec shows them.
 *
 * NEVER TALLER THAN THE WINDOW. It used to be exactly as tall as its content,
 * centred: a form longer than the window lost its title off the top and its
 * buttons off the bottom, and nothing scrolled - the one thing a dialog must
 * always be able to do is be answered. The header and the footer keep their
 * place; the body between them scrolls when it has to.
 */
export function Modal({
  open, onOpenChange, title, description, children, footer,
  size = 'md', tone = 'default', hideClose = false, className,
}: ModalProps) {
  const isPopup = tone === 'popup';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-overlay bg-foreground/40 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-modal flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl animate-scale-in',
            isPopup
              ? 'border border-popup-border bg-popup text-popup-foreground shadow-popup'
              : 'border border-border bg-card text-card-foreground shadow-modal',
            SIZE[size],
            className,
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 px-5 pt-5">
            <div className="min-w-0">
              <Dialog.Title className="text-2xl font-semibold">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description
                  className={cn('mt-1 text-md', isPopup ? 'text-popup-muted' : 'text-muted-foreground')}
                >
                  {description}
                </Dialog.Description>
              ) : null}
            </div>

            {!hideClose ? (
              <Dialog.Close
                aria-label="Close"
                className={cn(
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isPopup ? 'text-popup-muted hover:bg-white/10' : 'text-muted-foreground hover:bg-subtle',
                )}
              >
                <X size={18} strokeWidth={2} aria-hidden />
              </Dialog.Close>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <div
              className={cn(
                // Wraps: a footer with one action too many used to push its
                // first button out past the dialog's edge.
                'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-5 py-4',
                isPopup ? 'border-popup-border' : 'border-border',
              )}
            >
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
